import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus, Trash2, Eye, ArrowLeft, Search,
  FileText, ChevronDown, X, RefreshCw, Printer,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import { moneda, cantidad } from '../utils/format';
import { useAuth } from '../contexts/AuthContext';
import { render, printDocument } from '../utils/print.js';
import { PLANTILLAS_DEFAULT } from '../data/plantillasDefault.js';

// ── Buscador de clientes (typeahead) ──────────────────────────────────────
function ClienteSearch({ value, onChange }) {
  const [query,    setQuery]    = useState(value?.razon_social ?? '');
  const [results,  setResults]  = useState([]);
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/clientes/index.php', { params: { search: query, limit: 10 } });
        setResults(res.data);
        setOpen(true);
      } catch { /* silencioso */ }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const select = (cliente) => {
    onChange(cliente);
    setQuery(cliente.razon_social);
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-8 pr-8"
          placeholder="Buscar cliente por nombre o CUIT..."
          value={query}
          onChange={e => { setQuery(e.target.value); if (!e.target.value) clear(); }}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
        {(query || value) && (
          <button onClick={clear} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500">
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white rounded-xl shadow-dropdown ring-1 ring-slate-200 overflow-hidden">
          {loading ? (
            <div className="px-4 py-3 text-sm text-slate-400 flex items-center gap-2">
              <RefreshCw size={13} className="animate-spin" /> Buscando...
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-400">Sin resultados</div>
          ) : (
            results.map(c => (
              <button
                key={c.id}
                onMouseDown={() => select(c)}
                className="w-full text-left px-4 py-2.5 hover:bg-primary-50 transition-colors"
              >
                <p className="text-sm font-medium text-slate-800">{c.razon_social}</p>
                <p className="text-xs text-slate-400">{[c.cuit, c.condicion_iva?.replace(/_/g, ' ')].filter(Boolean).join(' · ')}</p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ── Buscador de productos (typeahead para la tabla de ítems) ──────────────
function ProductoPicker({ onSelect }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 1) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get('/productos/index.php', { params: { search: query } });
        setResults(res.data.filter(p => p.activo !== false));
        setOpen(true);
      } catch { /* silencioso */ }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const select = (producto) => {
    onSelect(producto);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-8 text-sm py-2"
          placeholder="Agregar producto..."
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 left-0 w-72 mt-1 bg-white rounded-xl shadow-dropdown ring-1 ring-slate-200 overflow-hidden max-h-56 overflow-y-auto">
          {results.map(p => {
            const sinStock = p.controla_stock && p.stock_actual <= 0;
            return (
              <button
                key={p.id}
                onMouseDown={() => !sinStock && select(p)}
                disabled={sinStock}
                className={`w-full text-left px-3 py-2 transition-colors ${sinStock ? 'opacity-40 cursor-not-allowed' : 'hover:bg-primary-50'}`}
              >
                <p className="text-sm font-medium text-slate-800">{p.nombre}</p>
                <p className="text-xs text-slate-400">
                  {moneda(p.precio_venta)}
                  {p.controla_stock && ` · Stock: ${cantidad(p.stock_actual, p.unidad)}`}
                  {sinStock && ' · Sin stock'}
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Modal detalle ─────────────────────────────────────────────────────────
function DetalleModal({ venta, onClose }) {
  if (!venta) return null;
  const fechaStr = new Date(venta.fecha).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });

  const handlePrint = async () => {
    let html = PLANTILLAS_DEFAULT.venta.html;
    try {
      const res = await api.get('/plantillas/index.php?tipo=venta');
      if (res.data?.html) html = res.data.html;
    } catch { /* usa default */ }

    const data = {
      numero: String(venta.numero ?? venta.id).padStart(4, '0'),
      fecha: fechaStr,
      cliente: venta.cliente || 'Mostrador',
      forma_pago: venta.forma_pago || '—',
      usuario: venta.usuario,
      subtotal_fmt: moneda(venta.subtotal),
      descuento_fmt: moneda(venta.descuento ?? 0),
      total_fmt: moneda(venta.total),
      items: (venta.items ?? []).map(item => ({
        ...item,
        precio_unitario: moneda(item.precio_unitario),
        subtotal: moneda(item.subtotal),
      })),
    };
    printDocument(render(html, data));
  };

  return (
    <Modal isOpen={!!venta} onClose={onClose} title={`Factura #${venta.numero ?? venta.id}`} size="md"
      footer={
        <div className="flex justify-between w-full">
          <button className="btn-ghost gap-1.5 text-sm" onClick={handlePrint}>
            <Printer size={14} /> Imprimir
          </button>
          <button className="btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          <div><span className="text-slate-400">Fecha:</span> <span className="font-medium">{fechaStr}</span></div>
          <div><span className="text-slate-400">Operador:</span> <span className="font-medium">{venta.usuario}</span></div>
          {venta.cliente && <div className="col-span-2"><span className="text-slate-400">Cliente:</span> <span className="font-medium">{venta.cliente}</span></div>}
          {venta.forma_pago && <div><span className="text-slate-400">Forma de pago:</span> <span className="font-medium">{venta.forma_pago}</span></div>}
        </div>

        <div className="table-wrapper">
          <table className="table">
            <thead><tr><th>Producto</th><th>Cant.</th><th>Precio</th><th>Subtotal</th></tr></thead>
            <tbody>
              {venta.items?.map((item, i) => (
                <tr key={i}>
                  <td className="font-medium">{item.producto}</td>
                  <td className="tabular-nums">{cantidad(item.cantidad, item.unidad)}</td>
                  <td className="tabular-nums">{moneda(item.precio_unitario)}</td>
                  <td className="tabular-nums font-medium">{moneda(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between text-slate-500"><span>Subtotal</span><span className="tabular-nums">{moneda(venta.subtotal)}</span></div>
          {venta.descuento > 0 && (
            <div className="flex justify-between text-emerald-600"><span>Descuento</span><span className="tabular-nums">-{moneda(venta.descuento)}</span></div>
          )}
          <div className="flex justify-between font-bold text-slate-900 text-base pt-2 border-t border-slate-100">
            <span>Total</span><span className="tabular-nums">{moneda(venta.total)}</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ── Formulario nueva factura ──────────────────────────────────────────────
function NuevaFactura({ onCancel, onSaved, formasPago }) {
  const [cliente,     setCliente]     = useState(null);
  const [fecha,       setFecha]       = useState(new Date().toISOString().slice(0, 10));
  const [formaPagoId, setFormaPagoId] = useState(() => {
    const ef = formasPago.find(f => f.tipo === 'efectivo');
    return ef ? String(ef.id) : '';
  });
  const [items,       setItems]       = useState([]);
  const [descuento,   setDescuento]   = useState('0');
  const [notas,       setNotas]       = useState('');
  const [saving,      setSaving]      = useState(false);

  const agregarProducto = (producto) => {
    setItems(prev => {
      const existe = prev.find(i => i.producto_id === producto.id);
      if (existe) {
        return prev.map(i =>
          i.producto_id === producto.id ? { ...i, cantidad: i.cantidad + 1 } : i
        );
      }
      return [...prev, {
        producto_id:     producto.id,
        nombre:          producto.nombre,
        unidad:          producto.unidad ?? '',
        precio_unitario: producto.precio_venta,
        cantidad:        1,
        stock_actual:    producto.stock_actual,
        controla_stock:  producto.controla_stock,
      }];
    });
  };

  const updateItem = (productoId, field, value) => {
    setItems(prev => prev.map(i =>
      i.producto_id === productoId ? { ...i, [field]: parseFloat(value) || 0 } : i
    ));
  };

  const removeItem = (productoId) => setItems(prev => prev.filter(i => i.producto_id !== productoId));

  const subtotal     = items.reduce((s, i) => s + i.precio_unitario * i.cantidad, 0);
  const descuentoNum = Math.min(parseFloat(descuento) || 0, subtotal);
  const total        = subtotal - descuentoNum;

  const handleSave = async () => {
    if (items.length === 0) { toast.error('Agregá al menos un producto'); return; }
    setSaving(true);
    try {
      await api.post('/ventas/index.php', {
        cliente_id:    cliente?.id ?? null,
        forma_pago_id: formaPagoId ? parseInt(formaPagoId) : null,
        descuento:     descuentoNum,
        notas,
        items: items.map(i => ({
          producto_id:     i.producto_id,
          cantidad:        i.cantidad,
          precio_unitario: i.precio_unitario,
        })),
      });
      toast.success('Factura guardada');
      onSaved();
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="btn-ghost p-2 -ml-2">
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="page-title">Nueva factura</h1>
          <p className="text-xs text-slate-400 mt-0.5">Los campos marcados con * son obligatorios</p>
        </div>
      </div>

      {/* Datos generales */}
      <div className="card space-y-4">
        <h2 className="text-sm font-bold text-slate-700">Datos generales</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label>Cliente</label>
            <ClienteSearch value={cliente} onChange={setCliente} />
            {!cliente && <p className="text-[11px] text-slate-400 mt-1">Opcional — si no se selecciona queda como venta de mostrador</p>}
          </div>
          <div>
            <label>Fecha *</label>
            <input className="input" type="date" value={fecha} onChange={e => setFecha(e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label>Forma de pago</label>
            <select className="input" value={formaPagoId} onChange={e => setFormaPagoId(e.target.value)}>
              <option value="">Sin especificar</option>
              {formasPago.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
          </div>
          <div>
            <label>Notas</label>
            <input className="input" placeholder="Observaciones opcionales..." value={notas} onChange={e => setNotas(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Ítems */}
      <div className="card space-y-4">
        <h2 className="text-sm font-bold text-slate-700">Productos *</h2>

        {/* Tabla de ítems */}
        {items.length > 0 && (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th className="w-1/2">Producto</th>
                  <th>Cantidad</th>
                  <th>Precio unit.</th>
                  <th>Subtotal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.producto_id}>
                    <td>
                      <p className="font-medium text-slate-900">{item.nombre}</p>
                      {item.controla_stock && (
                        <p className="text-[11px] text-slate-400">Stock disponible: {cantidad(item.stock_actual, item.unidad)}</p>
                      )}
                    </td>
                    <td>
                      <input
                        className="input text-sm py-1.5 w-24 text-right tabular-nums"
                        type="number" min="0.001" step="0.001"
                        value={item.cantidad}
                        onChange={e => updateItem(item.producto_id, 'cantidad', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="input text-sm py-1.5 w-28 text-right tabular-nums"
                        type="number" min="0" step="0.01"
                        value={item.precio_unitario}
                        onChange={e => updateItem(item.producto_id, 'precio_unitario', e.target.value)}
                      />
                    </td>
                    <td className="tabular-nums font-semibold text-slate-900">
                      {moneda(item.precio_unitario * item.cantidad)}
                    </td>
                    <td>
                      <button onClick={() => removeItem(item.producto_id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Picker de producto */}
        <div className="max-w-sm">
          <ProductoPicker onSelect={agregarProducto} />
        </div>

        {items.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-6 border-2 border-dashed border-slate-200 rounded-xl">
            Buscá un producto arriba para agregarlo a la factura
          </p>
        )}
      </div>

      {/* Totales y acciones */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-end justify-between">
        {/* Descuento */}
        <div className="flex items-center gap-3">
          <label className="!mb-0 w-24">Descuento $</label>
          <input
            className="input w-32 text-right tabular-nums"
            type="number" min="0" step="0.01"
            value={descuento}
            onChange={e => setDescuento(e.target.value)}
          />
        </div>

        {/* Resumen */}
        <div className="card-sm min-w-[220px] space-y-1.5">
          <div className="flex justify-between text-sm text-slate-500">
            <span>Subtotal</span>
            <span className="tabular-nums">{moneda(subtotal)}</span>
          </div>
          {descuentoNum > 0 && (
            <div className="flex justify-between text-sm text-emerald-600">
              <span>Descuento</span>
              <span className="tabular-nums">-{moneda(descuentoNum)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-slate-900 text-base pt-1.5 border-t border-slate-100">
            <span>Total</span>
            <span className="tabular-nums text-primary-600">{moneda(total)}</span>
          </div>
        </div>
      </div>

      {/* Botones */}
      <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
        <button className="btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
        <button className="btn-primary" onClick={handleSave} disabled={saving || items.length === 0}>
          {saving ? 'Guardando...' : 'Guardar factura'}
        </button>
      </div>
    </div>
  );
}

// ── Lista de facturas ─────────────────────────────────────────────────────
function ListaFacturas({ onNueva, formasPago }) {
  const [ventas,         setVentas]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [detalle,        setDetalle]        = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [fechaDesde,     setFechaDesde]     = useState('');
  const [fechaHasta,     setFechaHasta]     = useState('');

  const fetchVentas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const res = await api.get('/ventas/index.php', { params });
      setVentas(res.data);
    } catch { toast.error('Error al cargar facturas'); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { fetchVentas(); }, [fetchVentas]);

  const verDetalle = async (id) => {
    setLoadingDetalle(true);
    try {
      const res = await api.get(`/ventas/index.php?id=${id}`);
      setDetalle(res.data);
    } catch { toast.error('Error al cargar detalle'); }
    finally { setLoadingDetalle(false); }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Facturas de venta</h1>
        <button className="btn-primary" onClick={onNueva}>
          <Plus size={17} /> Nueva factura
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label>Desde</label>
          <input className="input" type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
        </div>
        <div>
          <label>Hasta</label>
          <input className="input" type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
        </div>
        {(fechaDesde || fechaHasta) && (
          <button className="btn-ghost text-sm" onClick={() => { setFechaDesde(''); setFechaHasta(''); }}>
            <X size={14} /> Limpiar
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : ventas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-300 select-none">
            <FileText size={48} strokeWidth={1.5} />
            <p className="text-sm mt-3 text-slate-400">No hay facturas en este período</p>
            <button className="btn-primary mt-4" onClick={onNueva}><Plus size={15} /> Nueva factura</button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Cliente</th>
                  <th>Forma de pago</th>
                  <th>Ítems</th>
                  <th>Descuento</th>
                  <th>Total</th>
                  <th>Operador</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {ventas.map(v => {
                  const fecha = new Date(v.fecha).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
                  return (
                    <tr key={v.id}>
                      <td className="font-mono text-xs text-slate-400">#{String(v.numero).padStart(4, '0')}</td>
                      <td className="text-slate-600 text-sm tabular-nums">{fecha}</td>
                      <td className="text-slate-700 text-sm">{v.cliente || <span className="text-slate-300 text-xs">Mostrador</span>}</td>
                      <td className="text-slate-500 text-sm">{v.forma_pago || <span className="text-slate-300">—</span>}</td>
                      <td className="text-slate-500">{v.num_items}</td>
                      <td className="tabular-nums text-emerald-600">
                        {v.descuento > 0 ? `-${moneda(v.descuento)}` : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="tabular-nums font-bold text-slate-900">{moneda(v.total)}</td>
                      <td className="text-slate-400 text-xs">{v.usuario}</td>
                      <td>
                        <button onClick={() => verDetalle(v.id)} disabled={loadingDetalle}
                          className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
                          <Eye size={15} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && ventas.length > 0 && (
        <p className="text-xs text-slate-400">{ventas.length} factura/s</p>
      )}

      <DetalleModal venta={detalle} onClose={() => setDetalle(null)} />
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function Facturas() {
  const [vista,      setVista]      = useState('lista'); // 'lista' | 'nueva'
  const [formasPago, setFormasPago] = useState([]);

  useEffect(() => {
    api.get('/formas-pago/index.php').then(r => setFormasPago(r.data)).catch(() => {});
  }, []);

  const handleSaved = () => setVista('lista');

  if (vista === 'nueva') {
    return (
      <NuevaFactura
        onCancel={() => setVista('lista')}
        onSaved={handleSaved}
        formasPago={formasPago}
      />
    );
  }

  return (
    <ListaFacturas
      onNueva={() => setVista('nueva')}
      formasPago={formasPago}
    />
  );
}
